import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Light/dark mode. Follows the device setting until the visitor picks one
// with the header toggle; that choice is remembered in localStorage.
// The mode is applied as <html data-theme="light|dark">, which the theme
// tokens in colors-light.css switch on. public/index.html applies it once
// before first paint, so there is no light flash on a dark device.

const STORAGE_KEY = 'theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const readSaved = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch {
    return null; // storage blocked (private window etc.)
  }
};

const systemMode = () => (window.matchMedia && window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light');

// Set before React re-renders, so components reading CSS tokens during
// render (the MUI theme in App.js) already see the new values.
const applyMode = (mode) => document.documentElement.setAttribute('data-theme', mode);

const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {} });

export const ThemeModeProvider = ({ children }) => {
  const [saved, setSaved] = useState(readSaved);
  const [system, setSystem] = useState(() => {
    const initial = systemMode();
    applyMode(readSaved() || initial);
    return initial;
  });

  // Keep following the device while the visitor hasn't picked a mode.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(DARK_QUERY);
    const onChange = (e) => {
      const next = e.matches ? 'dark' : 'light';
      if (!readSaved()) applyMode(next);
      setSystem(next);
    };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const mode = saved || system;

  useEffect(() => { applyMode(mode); }, [mode]);

  const toggleMode = useCallback(() => {
    const next = mode === 'dark' ? 'light' : 'dark';
    applyMode(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* not persisted */ }
    setSaved(next);
  }, [mode]);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);
  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};

export const useThemeMode = () => useContext(ThemeModeContext);
